import { FC } from "react";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import StepConnector, {
  stepConnectorClasses,
} from "@mui/material/StepConnector";
import { StepIconProps } from "@mui/material/StepIcon";
import Typography from "@mui/material/Typography";
import Card from "@mui/material/Card";
import CheckIcon from "@mui/icons-material/Check";
import { styled } from "@mui/material/styles";
import { inarixColors } from "inarix-theme";

import {
  cardSx,
  checkIconSx,
  DONE_COLOR,
  PENDING_COLOR,
  stepIconRootBase,
  stepLabelSx,
  stepperSx,
  subtitleSx,
  titleColor,
} from "./Timeline.styles";

export interface TimelineStep {
  title: string;
  subtitle?: string;
}

interface TimelineProps {
  steps: TimelineStep[];
  activeStep: number;
}

const InarixConnector = styled(StepConnector)(() => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 12,
  },
  [`&.${stepConnectorClasses.completed} .${stepConnectorClasses.line}`]: {
    backgroundColor: DONE_COLOR,
  },
  [`&.${stepConnectorClasses.active} .${stepConnectorClasses.line}`]: {
    backgroundColor: DONE_COLOR,
  },
  [`& .${stepConnectorClasses.line}`]: {
    height: 2,
    border: 0,
    backgroundColor: PENDING_COLOR,
    borderRadius: 1,
  },
}));

const StepIconRoot = styled("div")<{
  ownerState: { active: boolean; completed: boolean };
}>(({ ownerState }) => ({
  ...stepIconRootBase,
  backgroundColor: ownerState.completed
    ? DONE_COLOR
    : ownerState.active
      ? inarixColors.main_inarix
      : PENDING_COLOR,
}));

const InarixStepIcon: FC<StepIconProps> = ({
  active = false,
  completed = false,
  icon,
}) => (
  <StepIconRoot ownerState={{ active, completed }}>
    {completed ? <CheckIcon sx={checkIconSx} /> : icon}
  </StepIconRoot>
);

const Timeline: FC<TimelineProps> = ({ steps, activeStep }) => (
  <Card elevation={0} sx={cardSx}>
    <Stepper
      activeStep={activeStep}
      alternativeLabel
      connector={<InarixConnector />}
      sx={stepperSx}
    >
      {steps.map((step, index) => {
        const isCompleted = index < activeStep;
        const isActive = index === activeStep;

        return (
          <Step key={step.title} completed={isCompleted}>
            <StepLabel StepIconComponent={InarixStepIcon} sx={stepLabelSx}>
              <Typography
                variant="caption"
                fontWeight={isActive || isCompleted ? 700 : 400}
                color={titleColor(isCompleted, isActive)}
                lineHeight={1.2}
                display="block"
              >
                {step.title}
              </Typography>
              {step.subtitle && (
                <Typography
                  sx={subtitleSx}
                  color={isActive ? "text.secondary" : "text.disabled"}
                  lineHeight={1.2}
                  display="block"
                >
                  {step.subtitle}
                </Typography>
              )}
            </StepLabel>
          </Step>
        );
      })}
    </Stepper>
  </Card>
);

export default Timeline;
